import React, { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { RoomData, TotalData, WalletSummary, AggregatedData, TaxDetail } from './types'
import {
  calculateAggregatedTotals,
  getAllTotalsByPayment,
  getNotIncludedTaxCurrency,
  groupTaxDetails,
  getBattlefaceLabel,
} from './utils'

interface ComputedData {
  enrichedRooms: RoomData[]
  aggregatedData: AggregatedData | null
  groupedTaxDetails: TaxDetail[]
  paymentTotals: ReturnType<typeof getAllTotalsByPayment>
  payAtPropertyCurrency: string | undefined
  battlefaceProducts: Array<[string, number]>
}

interface DisplayFlags {
  displayInsuranceSection: boolean
  displayWalletSection: boolean
  displayBattlefaceSection: boolean
  displayPayAtPropertySection: boolean
  displayTaxDetailsButton: boolean
}

interface ResolvedTotals {
  totalPrice: number
  totalInsurance: number
  totalInsuranceCurrency: string | undefined
}

interface PriceDetailsContextType {
  nights: number
  rooms: RoomData[]
  totalData: TotalData | undefined
  wallet: WalletSummary | undefined
  onWalletToggle: ((nextSelected: boolean) => void) | undefined
  computed: ComputedData
  display: DisplayFlags
  totals: ResolvedTotals
}

const PriceDetailsContext = createContext<PriceDetailsContextType | null>(null)

interface PriceDetailsProviderProps {
  nights: number
  rooms: RoomData[]
  totalData?: TotalData
  wallet?: WalletSummary
  onWalletToggle?: (nextSelected: boolean) => void
  children: ReactNode
}

function enrichRoomsWithAvailability(rooms: RoomData[], totalData?: TotalData): RoomData[] {
  if (!totalData?.availabilities || totalData.availabilities.length === 0) {
    return rooms
  }

  return rooms.map((room, index) => {
    const matchingAvailability = totalData.availabilities?.[index]
    if (!matchingAvailability?.summary) {
      return room
    }

    const hasValidTaxes =
      matchingAvailability.taxes &&
      matchingAvailability.taxes.length > 0 &&
      matchingAvailability.taxes.some(tax => tax.amount > 0)

    if (!hasValidTaxes) {
      return {
        ...room,
        subtotal: matchingAvailability.summary.subtotal?.amount ?? room.subtotal,
        total: matchingAvailability.summary.total?.amount ?? room.total,
        totalWithFees: matchingAvailability.summary.total_with_fees?.amount ?? room.totalWithFees,
        taxDetails: [],
      }
    }

    const updatedTaxDetails = (matchingAvailability.taxes || [])
      .filter(tax => tax.amount > 0)
      .map(tax => ({
        amount: tax.amount,
        currency: tax.currency,
        included: tax.included,
        name: tax.name,
      }))

    return {
      ...room,
      subtotal: matchingAvailability.summary.subtotal?.amount ?? room.subtotal,
      total: matchingAvailability.summary.total?.amount ?? room.total,
      totalWithFees: matchingAvailability.summary.total_with_fees?.amount ?? room.totalWithFees,
      taxDetails: updatedTaxDetails,
    }
  })
}

function extractBattlefaceProducts(totalData?: TotalData): Array<[string, number]> {
  if (!totalData?.additionalProducts) {
    return []
  }
  return Object.entries(totalData.additionalProducts).filter(
    ([key, value]) => key.startsWith('battleface') && typeof value === 'number' && value > 0
  )
}

export function PriceDetailsProvider({
  nights,
  rooms,
  totalData,
  wallet,
  onWalletToggle,
  children,
}: PriceDetailsProviderProps) {
  const contextValue = useMemo<PriceDetailsContextType>(() => {
    const enrichedRooms = enrichRoomsWithAvailability(rooms, totalData)
    const aggregatedData =
      enrichedRooms && enrichedRooms.length > 0 ? calculateAggregatedTotals(enrichedRooms) : null
    const groupedTaxDetails = aggregatedData?.taxDetails
      ? groupTaxDetails(aggregatedData.taxDetails)
      : []
    const paymentTotals = getAllTotalsByPayment(groupedTaxDetails)
    const payAtPropertyCurrency = aggregatedData?.taxDetails
      ? getNotIncludedTaxCurrency(aggregatedData.taxDetails)
      : undefined
    const battlefaceProducts = extractBattlefaceProducts(totalData)
    const computed: ComputedData = {
      enrichedRooms,
      aggregatedData,
      groupedTaxDetails,
      paymentTotals,
      payAtPropertyCurrency,
      battlefaceProducts,
    }

    const display: DisplayFlags = {
      displayInsuranceSection: aggregatedData?.hasInsurance ?? false,
      displayWalletSection: Boolean(
        wallet && wallet.isVisible && wallet.availableAmount && wallet.availableAmount > 0
      ),
      displayBattlefaceSection: battlefaceProducts.length > 0,
      displayPayAtPropertySection: Boolean(paymentTotals.payLater > 0),
      displayTaxDetailsButton: Boolean(
        groupedTaxDetails &&
          groupedTaxDetails.length > 0 &&
          groupedTaxDetails.some(tax => (tax.client?.amount ?? tax.amount) > 0)
      ),
    }

    const totalPrice = totalData?.totalWithFees || aggregatedData?.totalWithInsurance || 0
    const totalInsurance = totalData?.insurance || aggregatedData?.insuranceAmount || 0
    const totalInsuranceCurrency = totalData?.insuranceCurrency || aggregatedData?.insuranceCurrency

    const totals: ResolvedTotals = {
      totalPrice,
      totalInsurance,
      totalInsuranceCurrency,
    }

    return {
      nights,
      rooms,
      totalData,
      wallet,
      onWalletToggle,
      computed,
      display,
      totals,
    }
  }, [nights, rooms, totalData, wallet, onWalletToggle])

  return (
    <PriceDetailsContext.Provider value={contextValue}>{children}</PriceDetailsContext.Provider>
  )
}
PriceDetailsProvider.displayName = 'PriceDetailsProvider'

export function usePriceDetailsContext(): PriceDetailsContextType {
  const context = useContext(PriceDetailsContext)
  if (!context) {
    throw new Error('usePriceDetailsContext must be used within a PriceDetailsProvider')
  }
  return context
}

export { getBattlefaceLabel }
